import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'template',
    'container',
    'newRoleIdInput'
  ]

  static values = {
    regexp: String,
    roleId: String
  }

  declare readonly templateTarget: HTMLTemplateElement
  declare readonly containerTarget: HTMLDivElement
  declare readonly hasRegexpValue: boolean
  declare readonly newRoleIdInputTarget: HTMLInputElement
  declare readonly newRoleIdInputTargets: HTMLInputElement[]
  declare readonly hasNewRoleIdInputTarget: boolean

  declare regexpValue: string
  declare roleIdValue: string

  connect (): void {
    document.addEventListener('statusChanged', () => this.toggleContainerVisibility())
    this.toggleContainerVisibility()
  }

  disconnect (): void {
    document.removeEventListener('statusChanged', () => this.toggleContainerVisibility())
  }

  add ({ detail: { id, roleLabel } }: CustomEvent<{ id: string, roleLabel?: string }>): void {
    if (this.containerTarget.classList.contains('hidden')) {
      this.containerTarget.classList.remove('hidden')
    }

    let html = this.templateTarget.innerHTML

    if (this.hasRegexpValue) {
      const newId = `${id || new Date().getTime()}` // eslint-disable-line @typescript-eslint/strict-boolean-expressions, @typescript-eslint/restrict-template-expressions
      const templateId = new RegExp(this.regexpValue, 'g')
      html = html.replace(templateId, newId)
    }

    // Replace "New Role Status Placeholder" with the provided roleLabel
    if (roleLabel != null) {
      html = html.replace('New Role Status Placeholder', `${roleLabel} `)
    }

    this.containerTarget.insertAdjacentHTML('beforeend', html)

    this.reattachPositionClasses()

    const newRole = this.newRoleIdInputTargets.find(input => input.value === '')

    if (newRole != null) {
      newRole.value = id ?? ''
    }
  }

  reattachPositionClasses (): void {
    const children = this.getContainerChildren().filter(e => !e.classList.contains('hidden'))

    children.forEach((child, index) => {
      child.classList.remove('first', 'last')
      if (index === 0) {
        child.classList.add('first')
      }
      if (index === children.length - 1) {
        child.classList.add('last')
      }
    })
  }

  getContainerChildren (): HTMLElement[] {
    const isContainerList = ['UL', 'OL', 'DL'].includes(this.containerTarget.tagName)
    const containerChildrenTagName = isContainerList ? 'LI' : 'DIV'

    return Array.from(this.containerTarget.children)
      .filter(child => child.tagName === containerChildrenTagName) as HTMLElement[]
  }

  isContainerEmpty (): boolean {
    return this.getContainerChildren().every(child => child.classList.contains('hidden'))
  }

  toggleContainerVisibility (): void {
    this.reattachPositionClasses()
    this.containerTarget.classList.toggle('hidden', this.isContainerEmpty())
  }
}
